/**
 * Tenant → Products drill-down.
 *
 * Lands here from the Tenants list. Shows one row per product the tenant
 * has emitted usage events for in the current window. Clicking a row
 * navigates one level deeper to per-instance breakdown.
 */
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CostWindowSelector from '@/components/tenants/CostWindowSelector';
import { useProductCosts } from '@/hooks/useTenantCosts';
import type { CostPeriod, CostWindowParams } from '@/api/tenants';

function formatUsd(s: string): string {
  const n = Number(s);
  if (Number.isNaN(n)) return '$0.00';
  if (n >= 100) return `$${n.toFixed(2)}`;
  if (n >= 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function paramsToWindow(p: URLSearchParams): CostWindowParams {
  const period = (p.get('period') as CostPeriod | null) ?? '30d';
  const start = p.get('start') ?? undefined;
  const end = p.get('end') ?? undefined;
  return { period, start, end };
}

function windowToParams(w: CostWindowParams): string {
  const p = new URLSearchParams();
  if (w.period) p.set('period', w.period);
  if (w.start) p.set('start', w.start);
  if (w.end) p.set('end', w.end);
  return p.toString();
}

export default function TenantProducts() {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [window, setWindowState] = useState<CostWindowParams>(() => paramsToWindow(searchParams));

  function setWindow(next: CostWindowParams) {
    setWindowState(next);
    setSearchParams(new URLSearchParams(windowToParams(next)));
  }

  const { data, isLoading, error } = useProductCosts(tenantId, window);

  const totalCost = useMemo(
    () => (data?.rows ?? []).reduce((sum, r) => sum + Number(r.total_cost_usd), 0),
    [data],
  );

  const drillSuffix = `?${windowToParams(window)}`;

  return (
    <div className="space-y-6 animate-fade-in">
      <nav className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Link to={`/tenants${drillSuffix}`} className="inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors">
          <ChevronLeft className="h-4 w-4" />
          Tenants
        </Link>
        <span className="text-slate-300 dark:text-slate-600">/</span>
        <span className="font-mono text-slate-700 dark:text-slate-200">{tenantId}</span>
      </nav>

      <header>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Products for tenant
        </h1>
        <p className="mt-1 font-mono text-sm text-slate-500 dark:text-slate-400">{tenantId}</p>
      </header>

      <CostWindowSelector value={window} onChange={setWindow} />

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          Failed to load products: {(error as Error).message}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200/60 bg-white shadow-card overflow-hidden dark:border-slate-800/80 dark:bg-slate-900">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-surface-50 dark:bg-slate-800/50">
            <tr>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Product
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Cost
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Events
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Services
              </th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Last Activity
              </th>
              <th className="w-8 px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && (data?.rows.length ?? 0) === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No products in this window for this tenant.
                </td>
              </tr>
            )}
            {data?.rows.map((row) => (
              <tr key={row.product} className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors">
                <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {row.product}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-900 dark:text-slate-100">
                  {formatUsd(row.total_cost_usd)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">
                  {row.event_count.toLocaleString()}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">
                  {row.distinct_instances}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                  {formatTime(row.last_event_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/tenants/${encodeURIComponent(tenantId)}/products/${encodeURIComponent(row.product)}${drillSuffix}`}
                    className="inline-flex items-center text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
          {(data?.rows.length ?? 0) > 0 && (
            <tfoot className="bg-surface-50 dark:bg-slate-800/50">
              <tr>
                <td className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Total
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-slate-900 dark:text-slate-100">
                  ${totalCost.toFixed(2)}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
